import { useState, useEffect } from 'react';
import { getUserErrorMessage } from '../utils/errorMessages';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  RefreshCcw, 
  TrendingUp,
  TrendingDown,
  Minus,
  History,
  Play,
  CheckCircle,
  Clock,
  Camera,
  ArrowRight,
  BarChart3,
  Loader2,
  Calendar,
  Target,
  Shield,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const ReAssessmentPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [reassessments, setReassessments] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [showStartModal, setShowStartModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [starting, setStarting] = useState(false);
  const [activeReassessment, setActiveReassessment] = useState(null);
  const [deltaChanges, setDeltaChanges] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [historyRes, reassessRes, compareRes, deltaRes] = await Promise.all([
        api.get('/assessments/history'),
        api.get('/assessments/reassessments'),
        api.get('/assessments/compare'),
        api.get('/assessments/detect-changes').catch(() => ({ data: null }))
      ]);
      
      setHistory(historyRes.data.snapshots || []);
      setReassessments(reassessRes.data || []);
      setComparison(compareRes.data);
      setDeltaChanges(deltaRes.data);
      
      // Check for active reassessment
      const active = reassessRes.data?.find(r => r.status === 'in_progress');
      setActiveReassessment(active);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('De gegevens konden niet worden geladen. Ververs de pagina.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartReassessment = async () => {
    setStarting(true);
    try {
      const response = await api.post('/assessments/reassess', {
        modules: ['all'],
        notes: 'Handmatige re-assessment gestart'
      });
      
      toast.success('Steekproef gestart!');
      setShowStartModal(false);
      setActiveReassessment({
        id: response.data.reassessment_id,
        status: 'in_progress',
        modules: ['all'],
        started_at: new Date().toISOString()
      });
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De steekproef kon niet worden gestart. Probeer het later opnieuw.'));
    } finally {
      setStarting(false);
    }
  };

  const handleCompleteReassessment = async () => {
    if (!activeReassessment) return;
    
    try {
      const response = await api.post(`/assessments/reassess/${activeReassessment.id}/complete`);
      
      toast.success('Steekproef afgerond!');
      setActiveReassessment(null);
      
      // Show results
      setComparison({
        current: response.data.after,
        previous: response.data.before,
        comparison: response.data.improvement
      });
      setShowCompareModal(true);
      
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De steekproef kon niet worden afgerond. Probeer het later opnieuw.'));
    }
  };

  const handleCreateSnapshot = async () => {
    try {
      await api.post('/assessments/snapshot', { notes: 'Handmatige snapshot' });
      toast.success('Snapshot aangemaakt');
      fetchData();
    } catch (error) {
      toast.error('De momentopname kon niet worden aangemaakt. Probeer het later opnieuw.');
    }
  };

  const handleViewComparison = async (snapshotId) => {
    try {
      const response = await api.get(`/assessments/compare?snapshot_id_1=${snapshotId}`);
      setComparison(response.data);
      setShowCompareModal(true);
    } catch (error) {
      toast.error('De vergelijking kon niet worden geladen. Probeer het later opnieuw.');
    }
  };

  const getScoreChangeIcon = (change) => {
    if (change > 0) return <TrendingUp className="h-4 w-4 text-green-500" strokeWidth={1.5} />;
    if (change < 0) return <TrendingDown className="h-4 w-4 text-red-500" strokeWidth={1.5} />;
    return <Minus className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />;
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 40) return 'text-orange-500';
    return 'text-red-500';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 lg:p-8 space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1">
              Voortgang
            </p>
            <h1 className="text-2xl font-bold tracking-tight text-black">
              Steekproeven
            </h1>
            <p className="text-sm text-neutral-600 mt-1">
              Meet uw vooruitgang en verbeteringen over tijd
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              className="border-neutral-200"
              onClick={handleCreateSnapshot}
              data-testid="create-snapshot-button"
            >
              <Camera className="mr-2 h-4 w-4" strokeWidth={1.5} />
              Snapshot
            </Button>
            {activeReassessment ? (
              <Button 
                className="bg-green-600 text-white hover:bg-green-700"
                onClick={handleCompleteReassessment}
                data-testid="complete-reassessment-button"
              >
                <CheckCircle className="mr-2 h-4 w-4" strokeWidth={1.5} />
                Afronden
              </Button>
            ) : (
              <Button 
                className="bg-black text-white hover:bg-neutral-800"
                onClick={() => setShowStartModal(true)}
                data-testid="start-reassessment-button"
              >
                <RefreshCcw className="mr-2 h-4 w-4" strokeWidth={1.5} />
                Start Steekproef
              </Button>
            )}
          </div>
        </div>

        {/* Active Re-Assessment Banner */}
        {activeReassessment && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <RefreshCcw className="h-6 w-6 text-blue-600 animate-spin" strokeWidth={1.5} style={{ animationDuration: '3s' }} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-blue-900">Steekproef Actief</h3>
                  <p className="text-sm text-blue-700">
                    Voer nieuwe scans uit in Mensen, Processen en Technologie modules om uw voortgang te meten.
                  </p>
                </div>
                <Button 
                  className="bg-blue-600 text-white hover:bg-blue-700"
                  onClick={handleCompleteReassessment}
                >
                  Voltooien & Vergelijken
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delta Detection - New/Changed Items */}
        {deltaChanges?.has_changes && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-amber-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <CardTitle className="text-base font-semibold text-amber-900">Nieuwe Items Gedetecteerd</CardTitle>
                    <CardDescription className="text-amber-700">
                      Er zijn {deltaChanges.new_items?.total || 0} nieuwe items sinds uw laatste assessment
                    </CardDescription>
                  </div>
                </div>
                <Button 
                  size="sm"
                  className="bg-amber-600 text-white hover:bg-amber-700"
                  onClick={() => setShowStartModal(true)}
                  data-testid="start-delta-reassessment-button"
                >
                  <RefreshCcw className="mr-2 h-4 w-4" strokeWidth={1.5} />
                  Re-assess Nu
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 rounded-lg bg-white border border-amber-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Shield className="h-4 w-4 text-amber-600" strokeWidth={1.5} />
                    <span className="text-xs font-medium text-amber-800">Assets</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-900">{deltaChanges.new_items?.assets || 0}</p>
                  <p className="text-xs text-amber-600">nieuwe items</p>
                </div>
                <div className="p-3 rounded-lg bg-white border border-amber-200">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="h-4 w-4 text-amber-600" strokeWidth={1.5} />
                    <span className="text-xs font-medium text-amber-800">Vulnerabilities</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-900">{deltaChanges.new_items?.vulnerabilities || 0}</p>
                  <p className="text-xs text-amber-600">nieuwe items</p>
                </div>
                <div className="p-3 rounded-lg bg-white border border-amber-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Target className="h-4 w-4 text-amber-600" strokeWidth={1.5} />
                    <span className="text-xs font-medium text-amber-800">Threats</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-900">{deltaChanges.new_items?.threats || 0}</p>
                  <p className="text-xs text-amber-600">nieuwe items</p>
                </div>
                <div className="p-3 rounded-lg bg-white border border-amber-200">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="h-4 w-4 text-amber-600" strokeWidth={1.5} />
                    <span className="text-xs font-medium text-amber-800">Maatregelen</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-900">{deltaChanges.new_items?.measures || 0}</p>
                  <p className="text-xs text-amber-600">nieuwe items</p>
                </div>
              </div>
              {deltaChanges.framework_changes?.unmapped_controls > 0 && (
                <div className="mt-3 p-3 rounded-lg bg-white border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <span className="font-medium">{deltaChanges.framework_changes.unmapped_controls}</span> framework controls zijn nog niet geëvalueerd.
                  </p>
                </div>
              )}
              {deltaChanges.last_assessment && (
                <p className="text-xs text-amber-600 mt-3">
                  Laatste assessment: {formatDate(deltaChanges.last_assessment)}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Current vs Previous Comparison */}
        {comparison?.current && comparison?.previous && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-neutral-500">CWI Score</span>
                  {getScoreChangeIcon(comparison.current.cwi_score - comparison.previous.cwi_score)}
                </div>
                <div className="flex items-baseline gap-2">
                  <span className={`text-4xl font-bold ${getScoreColor(comparison.current.cwi_score)}`}>
                    {comparison.current.cwi_score}
                  </span>
                  <span className="text-neutral-400">/ 100</span>
                </div>
                <div className="mt-2 text-sm">
                  <span className={comparison.current.cwi_score > comparison.previous.cwi_score ? 'text-green-600' : comparison.current.cwi_score < comparison.previous.cwi_score ? 'text-red-600' : 'text-neutral-500'}>
                    {comparison.current.cwi_score > comparison.previous.cwi_score ? '+' : ''}
                    {comparison.current.cwi_score - comparison.previous.cwi_score} punten
                  </span>
                  <span className="text-neutral-400"> vs vorige</span>
                </div>
                <Progress 
                  value={comparison.current.cwi_score} 
                  className="mt-4 h-2"
                />
              </CardContent>
            </Card>

            <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-neutral-500">Risico Blootstelling</span>
                  {getScoreChangeIcon(comparison.previous.total_exposure - comparison.current.total_exposure)}
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-black">
                    {formatCurrency(comparison.current.total_exposure)}
                  </span>
                </div>
                <div className="mt-2 text-sm">
                  <span className={comparison.current.total_exposure < comparison.previous.total_exposure ? 'text-green-600' : comparison.current.total_exposure > comparison.previous.total_exposure ? 'text-red-600' : 'text-neutral-500'}>
                    {comparison.current.total_exposure < comparison.previous.total_exposure ? '-' : '+'}
                    {formatCurrency(Math.abs(comparison.current.total_exposure - comparison.previous.total_exposure))}
                  </span>
                  <span className="text-neutral-400"> vs vorige</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-neutral-500">Gemitigeerd</span>
                  {getScoreChangeIcon(comparison.current.mitigated_exposure - comparison.previous.mitigated_exposure)}
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-green-600">
                    {formatCurrency(comparison.current.mitigated_exposure)}
                  </span>
                </div>
                <div className="mt-2 text-sm">
                  <span className="text-green-600">
                    +{formatCurrency(comparison.current.mitigated_exposure - comparison.previous.mitigated_exposure)}
                  </span>
                  <span className="text-neutral-400"> bespaard</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Assessment Timeline */}
        <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">
                Assessment Tijdlijn
              </CardTitle>
              <CardDescription className="text-sm mt-1">
                Historische snapshots van uw security status
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            {history.length > 0 ? (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-6 top-0 bottom-0 w-px bg-neutral-200" />
                
                <div className="space-y-4">
                  {history.map((snapshot, index) => (
                    <div key={snapshot.id} className="relative flex items-start gap-4 pl-12">
                      {/* Timeline dot */}
                      <div className={`absolute left-4 w-4 h-4 rounded-full border-2 ${
                        index === 0 ? 'bg-black border-black' : 'bg-white border-neutral-300'
                      }`} />
                      
                      <div className="flex-1 bg-neutral-50 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-black">
                                CWI Score: {snapshot.cwi_score}
                              </span>
                              <Badge variant="outline" className={`text-xs ${
                                snapshot.trigger === 'manual' ? 'border-blue-200 text-blue-700' :
                                snapshot.trigger === 'reassessment_complete' ? 'border-green-200 text-green-700' :
                                'border-neutral-200 text-neutral-600'
                              }`}>
                                {snapshot.trigger === 'manual' ? 'Handmatig' :
                                 snapshot.trigger === 'reassessment_start' ? 'Steekproef Start' :
                                 snapshot.trigger === 'reassessment_complete' ? 'Steekproef Klaar' :
                                 snapshot.trigger}
                              </Badge>
                            </div>
                            <p className="text-xs text-neutral-500 flex items-center gap-1">
                              <Calendar className="h-3 w-3" strokeWidth={1.5} />
                              {formatDate(snapshot.timestamp)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-black">
                              {formatCurrency(snapshot.total_exposure)}
                            </p>
                            <p className="text-xs text-neutral-500">blootstelling</p>
                          </div>
                        </div>
                        
                        <div className="mt-3 flex items-center gap-4">
                          <div className="flex items-center gap-1">
                            <div className="h-2 w-2 rounded-full bg-blue-500" />
                            <span className="text-xs text-neutral-600">
                              Mensen: {snapshot.people_score ?? '-'}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="h-2 w-2 rounded-full bg-purple-500" />
                            <span className="text-xs text-neutral-600">
                              Processen: {snapshot.process_score ?? '-'}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="h-2 w-2 rounded-full bg-green-500" />
                            <span className="text-xs text-neutral-600">
                              Technologie: {snapshot.technology_score ?? '-'}
                            </span>
                          </div>
                        </div>

                        {index > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="mt-2 text-xs"
                            onClick={() => handleViewComparison(snapshot.id)}
                          >
                            <BarChart3 className="h-3 w-3 mr-1" strokeWidth={1.5} />
                            Vergelijk met nu
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="py-12 text-center">
                <History className="h-12 w-12 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-lg font-medium text-black mb-1">Geen historie</h3>
                <p className="text-sm text-neutral-500 mb-4">
                  Maak een snapshot om uw security status bij te houden
                </p>
                <Button 
                  variant="outline"
                  className="border-neutral-200"
                  onClick={handleCreateSnapshot}
                >
                  <Camera className="mr-2 h-4 w-4" strokeWidth={1.5} />
                  Eerste Snapshot
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Previous Re-Assessments */}
        {reassessments.filter(r => r.status === 'completed').length > 0 && (
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">
                Afgeronde Steekproeven
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {reassessments.filter(r => r.status === 'completed').map((reassessment) => (
                  <div key={reassessment.id} className="flex items-center gap-4 p-4 bg-neutral-50 rounded-lg">
                    <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-green-600" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-black">
                        Steekproef {formatDate(reassessment.completed_at)}
                      </p>
                      <p className="text-xs text-neutral-500">
                        Modules: {reassessment.modules?.join(', ') || 'Alle'}
                      </p>
                    </div>
                    {reassessment.results && (
                      <div className="text-right">
                        <p className={`text-sm font-bold ${
                          reassessment.results.score_improvement > 0 ? 'text-green-600' : 
                          reassessment.results.score_improvement < 0 ? 'text-red-600' : 'text-neutral-600'
                        }`}>
                          {reassessment.results.score_improvement > 0 ? '+' : ''}
                          {reassessment.results.score_improvement} punten
                        </p>
                        <p className="text-xs text-neutral-500">
                          {formatCurrency(reassessment.results.exposure_reduction)} bespaard
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Start Re-Assessment Modal */}
      <Dialog open={showStartModal} onOpenChange={setShowStartModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Start Steekproef</DialogTitle>
            <DialogDescription>
              Meet uw voortgang door alle domeinen opnieuw te doorlopen en te vergelijken met de vorige meting.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Wat gebeurt er?</h4>
              <ol className="text-sm text-blue-700 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="font-bold">1.</span>
                  Huidige status wordt opgeslagen als "voor" snapshot
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">2.</span>
                  Voer nieuwe scans uit in de modules
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">3.</span>
                  Klik op "Afronden" om resultaten te vergelijken
                </li>
              </ol>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-black">Aanbevolen acties:</p>
              <div className="space-y-2">
                {[
                  { icon: Shield, label: 'Mensen', desc: 'Nieuwe phishing campagne' },
                  { icon: Target, label: 'Processen', desc: 'Vragenlijst opnieuw invullen' },
                  { icon: AlertTriangle, label: 'Technologie', desc: 'Nieuwe kwetsbaarheidsscan' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg">
                    <item.icon className="h-5 w-5 text-neutral-500" strokeWidth={1.5} />
                    <div>
                      <p className="text-sm font-medium text-black">{item.label}</p>
                      <p className="text-xs text-neutral-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStartModal(false)}>
              Annuleren
            </Button>
            <Button 
              className="bg-black text-white hover:bg-neutral-800"
              onClick={handleStartReassessment}
              disabled={starting}
              data-testid="confirm-start-reassessment"
            >
              {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <>
                  <Play className="mr-2 h-4 w-4" strokeWidth={1.5} />
                  Starten
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comparison Results Modal */}
      <Dialog open={showCompareModal} onOpenChange={setShowCompareModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Vergelijking Resultaten</DialogTitle>
            <DialogDescription>
              Uw voortgang vergeleken met de vorige assessment
            </DialogDescription>
          </DialogHeader>
          
          {comparison?.current && comparison?.previous && (
            <div className="py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-neutral-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-neutral-500 mb-1">Vorige Score</p>
                  <p className="text-3xl font-bold text-neutral-400">{comparison.previous.cwi_score}</p>
                </div>
                <div className="bg-neutral-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-neutral-500 mb-1">Huidige Score</p>
                  <p className={`text-3xl font-bold ${getScoreColor(comparison.current.cwi_score)}`}>
                    {comparison.current.cwi_score}
                  </p>
                </div>
              </div>

              <div className={`p-4 rounded-lg ${
                comparison.current.cwi_score > comparison.previous.cwi_score 
                  ? 'bg-green-50 border border-green-200' 
                  : comparison.current.cwi_score < comparison.previous.cwi_score
                  ? 'bg-red-50 border border-red-200'
                  : 'bg-neutral-50 border border-neutral-200'
              }`}>
                <div className="flex items-center gap-3">
                  {comparison.current.cwi_score > comparison.previous.cwi_score ? (
                    <TrendingUp className="h-8 w-8 text-green-600" strokeWidth={1.5} />
                  ) : comparison.current.cwi_score < comparison.previous.cwi_score ? (
                    <TrendingDown className="h-8 w-8 text-red-600" strokeWidth={1.5} />
                  ) : (
                    <Minus className="h-8 w-8 text-neutral-500" strokeWidth={1.5} />
                  )}
                  <div>
                    <p className="text-lg font-bold">
                      {comparison.current.cwi_score > comparison.previous.cwi_score ? '+' : ''}
                      {comparison.current.cwi_score - comparison.previous.cwi_score} punten
                    </p>
                    <p className="text-sm text-neutral-600">
                      {comparison.current.cwi_score > comparison.previous.cwi_score 
                        ? 'Uw security is verbeterd!' 
                        : comparison.current.cwi_score < comparison.previous.cwi_score
                        ? 'Er is ruimte voor verbetering'
                        : 'Score is gelijk gebleven'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Risico Reductie</span>
                  <span className="font-medium text-green-600">
                    {formatCurrency(Math.max(0, comparison.previous.total_exposure - comparison.current.total_exposure))}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Nieuwe Blootstelling</span>
                  <span className="font-medium">{formatCurrency(comparison.current.total_exposure)}</span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowCompareModal(false)}>
              Sluiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default ReAssessmentPage;
